import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient, ApiError, logoutSession } from '@/lib/api';
import { useAuthStore, isAdminRole } from '@/lib/auth-store';
import type { MeView } from '@/lib/types';
import type { RecoverySession } from '@/lib/auth-contract';

/**
 * Issue #21 / #38（P1）：超级管理员恢复码应急登录（**Web-only**）。
 *
 * 短信不可用时的应急通道：密码 + 一次性离线恢复码 + 原因。成功签发**最长 120 分钟、
 * 无 refresh token、不建立可信设备**的受限会话——access token 仅存内存，到期即须重新走
 * 应急流程。任何失败一律通用错误（不泄露账号 / 角色存在性）。红线：口令 / 恢复码绝不落
 * localStorage / 日志 / URL。移动端不暴露本入口。
 *
 * Issue #38（P1）：Web 资格改由**服务端事实**绑定——先 POST /auth/recovery-bootstrap（浏览器
 * 自动带 Origin，后端校验白名单后下发 HttpOnly bootstrap cookie + 返回 CSRF token），再 POST
 * /auth/recovery-login 时把 CSRF token 放进 X-Recovery-CSRF 头（cookie 由浏览器随附）。不再发送
 * 可伪造的请求体自报字段（Web 资格完全由 Origin + cookie + CSRF 决定）。
 */
export function RecoveryPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // P1 第一步：握手 bootstrap——后端校验 Origin 命中白名单后下发 HttpOnly bootstrap cookie，
      // 响应体回 CSRF token（cookie 由浏览器自动携带，JS 不可读）。
      const bootstrap = await apiClient.post<{ csrfToken: string; expiresInSec: number }>(
        '/auth/recovery-bootstrap',
      );
      // P1 第二步：带 X-Recovery-CSRF 头（double-submit）+ bootstrap cookie 提交正式恢复登录；
      // 不再发送请求体 client（Web 资格由服务端 Origin + cookie + CSRF 事实决定）。
      const session = await apiClient.request<RecoverySession>('/auth/recovery-login', {
        method: 'POST',
        headers: { 'X-Recovery-CSRF': bootstrap.csrfToken },
        body: JSON.stringify({
          phone: phone.trim(),
          password,
          recoveryCode: recoveryCode.trim(),
          reason: reason.trim(),
        }),
      });
      if (!session?.accessToken) {
        throw new ApiError('应急登录响应缺少令牌', 502);
      }
      setSession({ accessToken: session.accessToken, user: null });
      const me = await apiClient.get<MeView>('/auth/me');
      if (!isAdminRole(me.role)) {
        await logoutSession();
        setError('该账号无权使用应急登录。');
        return;
      }
      setSession({ accessToken: session.accessToken, user: me });
      navigate('/overview', { replace: true });
    } catch (err: unknown) {
      let message = '应急登录失败，请核对密码、恢复码与原因后重试';
      if (err instanceof ApiError) {
        // 401 通用错误（不泄露账号 / 角色 / 恢复码是否存在）。
        message = err.status === 401 ? '凭据或恢复码无效' : err.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-800 via-slate-700 to-slate-500 p-6">
      <div
        className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl"
        data-testid="recovery-card"
      >
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-700 text-white">
            应急
          </div>
          <h1 className="text-xl font-semibold text-slate-900">
            超级管理员恢复码登录
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            仅在短信不可用时使用；恢复码一次性、使用后立即失效
          </p>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">手机号</span>
            <input
              type="tel"
              autoComplete="username"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="超级管理员手机号"
              data-testid="recovery-phone"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">密码</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="请输入密码"
              data-testid="recovery-password"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">恢复码</span>
            <input
              type="text"
              autoComplete="off"
              required
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-base tracking-wider focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="XXXXX-XXXXX-XXXXX"
              data-testid="recovery-code"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">使用原因（必填，将写入审计与告警）</span>
            <textarea
              required
              minLength={4}
              maxLength={255}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="例如：短信网关故障，需应急处理生产事故"
              rows={2}
              data-testid="recovery-reason"
            />
          </label>
          {error ? (
            <div
              role="alert"
              data-testid="recovery-error"
              className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
            >
              {error}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={submitting || reason.trim().length < 4}
            className="w-full rounded-lg bg-slate-700 px-4 py-2 text-base font-medium text-white shadow transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="recovery-submit"
          >
            {submitting ? '登录中…' : '应急登录（120 分钟受限会话）'}
          </button>
          <div className="pt-1 text-center text-xs text-slate-400">
            <Link to="/login" className="hover:text-slate-600 hover:underline">
              返回常规登录
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
